import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { removeTeamMember } from '@/utils/teamMembership';

export interface RemoveTeamMemberButtonProps {
  teamId: number;
  userId: number;
  displayName: string;
  isDark: boolean;
  onSuccess?: () => void;
  /** Coach roster vs athlete — affects default title */
  memberKind?: 'athlete' | 'coach';
  /** compact: icon-only; default shows "Remove" on sm+ */
  variant?: 'default' | 'compact';
  className?: string;
}

const RemoveTeamMemberButton: React.FC<RemoveTeamMemberButtonProps> = ({
  teamId,
  userId,
  displayName,
  isDark,
  onSuccess,
  memberKind = 'athlete',
  variant = 'default',
  className = '',
}) => {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title =
    memberKind === 'coach' ? 'Remove coach from team' : 'Remove athlete from team';

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await removeTeamMember(teamId, userId);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  const baseBtn =
    variant === 'compact'
      ? `flex-shrink-0 flex items-center justify-center rounded-lg border transition-colors ${
          isDark
            ? 'border-gray-600 hover:bg-red-900/30 hover:border-red-500/50 text-gray-400 hover:text-red-400'
            : 'border-gray-300 hover:bg-red-50 hover:border-red-300 text-gray-400 hover:text-red-500'
        }`
      : `inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
          isDark
            ? 'border-red-800/60 text-red-400 hover:bg-red-950/50 hover:border-red-600'
            : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
        }`;

  if (confirming) {
    return (
      <div
        className={`flex flex-wrap items-center gap-1 ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="group"
        aria-label="Confirm removal"
      >
        {error && (
          <span className="text-[10px] text-red-500 w-full">{error}</span>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
        >
          {loading ? '…' : 'Confirm'}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setConfirming(false);
            setError(null);
          }}
          className="px-2 py-1 text-xs font-medium cgm-text-secondary rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      title={title}
      aria-label={`${memberKind === 'coach' ? 'Remove coach' : 'Remove athlete'} ${displayName}`}
      className={`${baseBtn} ${variant === 'compact' ? 'w-8 h-8' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setConfirming(true);
        setError(null);
      }}
    >
      <Trash2 className={variant === 'compact' ? 'w-3.5 h-3.5' : 'w-3.5 h-3.5'} strokeWidth={2} />
      {variant === 'default' && <span className="hidden sm:inline">Remove</span>}
    </button>
  );
};

export default RemoveTeamMemberButton;
